'use client';

import { useEffect, useState } from 'react';
import { ToggleCard } from '@pixum/toggle-card';
import Text, { TEXT_TAG, TEXT_VARIANT } from '@pixum/text';
import { RichTextBody, isRichTextEmpty } from 'components';
import { useCartPaymentFormStore } from 'stores/cart';
import { CartHintSnackbar } from '../../../partials/CartHintSnackbar';
import { CART_HINT_SNACKBAR_VARIANT } from '../../../constants/cartHintSnackbar';
import { trackPaymentAvailability } from '../../../analytics/payment';
import { getPaymentIcons } from '../../../utils/payment/getPaymentIcons';
import { readSubmittedPaymentType } from '../../../utils/payment/readSubmittedPaymentType';
import { PAYMENT_FORM_FIELD, PAYMENT_LABEL_KEYS, PAYMENT_METHOD, PAYMENT_TITLE_ID } from '../../constants';
import type { CartPaymentViewModel, CreditCardType, PaymentDescriptions, SepaValues } from '../../types';
import { CreditCardPaymentMethod } from './partials/CreditCardPaymentMethod';
import { SepaPaymentMethod } from './partials/SepaPaymentMethod';
import styles from './PaymentMethodsClient.module.css';

interface PaymentMethodsClientProps {
  viewModel: CartPaymentViewModel;
  isPaymentNeeded: boolean;
  constants: Record<string, string>;
  descriptions: PaymentDescriptions;
}

const EMPTY_SEPA_VALUES: SepaValues = { holder: '', iban: '', bic: '', bankName: '', accepted: false };

export function PaymentMethodsClient({
  viewModel,
  isPaymentNeeded,
  constants,
  descriptions
}: PaymentMethodsClientProps) {
  const {
    options,
    selectedMethod: preSelectedMethod,
    defaultTrackingMethod,
    hasPaymentMethodsRestricted,
    hasInvoice
  } = viewModel;
  const sepaErrors = useCartPaymentFormStore(store => store.fieldErrors);
  const clearSepaError = useCartPaymentFormStore(store => store.clearFieldError);
  const setHasPaymentMethods = useCartPaymentFormStore(store => store.setHasPaymentMethods);

  const [selectedMethod, setSelectedMethod] = useState(preSelectedMethod);
  const [hasUserSelected, setHasUserSelected] = useState(false);
  const [sepaValues, setSepaValues] = useState(EMPTY_SEPA_VALUES);
  const [selectedCreditCardType, setSelectedCreditCardType] = useState<CreditCardType | null>(null);

  const selectedOption = options.find(option => option.method === selectedMethod);
  const selectedType = readSubmittedPaymentType(selectedOption, selectedCreditCardType);
  // Gatsby tracks gui_pp_express until the customer picks a card themselves.
  const trackingMethod = (!hasUserSelected && defaultTrackingMethod) || (selectedMethod ?? '');

  useEffect(() => {
    trackPaymentAvailability(hasPaymentMethodsRestricted, hasInvoice);
  }, [hasPaymentMethodsRestricted, hasInvoice]);

  // The continue buttons live outside the Suspense boundary and stay disabled until these cards are shown.
  useEffect(() => {
    setHasPaymentMethods(true);

    return () => setHasPaymentMethods(false);
  }, [setHasPaymentMethods]);

  const isPaymentNeededInput = (
    <input type="hidden" name={PAYMENT_FORM_FIELD.IS_PAYMENT_NEEDED} value={String(isPaymentNeeded)} />
  );

  if (!isPaymentNeeded) {
    return (
      <div role="radiogroup" aria-labelledby={PAYMENT_TITLE_ID} className={styles.paymentMethods}>
        <ToggleCard selected label={constants['i18n_label_payment_not_required']} showIndicator>
          <Text variant={TEXT_VARIANT.SUBHEADLINE} tag={TEXT_TAG.P}>
            {constants['i18n_payment_not_required']}
          </Text>
        </ToggleCard>
        {isPaymentNeededInput}
      </div>
    );
  }

  return (
    <>
      <div role="radiogroup" aria-labelledby={PAYMENT_TITLE_ID} className={styles.paymentMethods}>
        {options.map(option => {
          const isSelected = option.method === selectedMethod;
          const description = descriptions[option.method];

          return (
            <ToggleCard
              key={option.method}
              selected={isSelected}
              onChange={() => {
                setSelectedMethod(option.method);
                setHasUserSelected(true);
              }}
              label={constants[PAYMENT_LABEL_KEYS[option.method]]}
              showIndicator
              topRight={getPaymentIcons(option)}
            >
              {isSelected && !isRichTextEmpty(description) && (
                <RichTextBody text={description} wrapTextVariant={TEXT_VARIANT.SUBHEADLINE} />
              )}

              {isSelected && option.method === PAYMENT_METHOD.CREDIT_CARD && (
                <CreditCardPaymentMethod
                  types={option.types}
                  value={selectedCreditCardType ?? option.selectedType}
                  onChange={setSelectedCreditCardType}
                  constants={constants}
                />
              )}

              {isSelected && option.method === PAYMENT_METHOD.SEPA && (
                <SepaPaymentMethod
                  constants={constants}
                  values={sepaValues}
                  errors={sepaErrors}
                  onChange={(field, value) => {
                    setSepaValues(current => ({ ...current, [field]: value }));
                    clearSepaError(field);
                  }}
                />
              )}
            </ToggleCard>
          );
        })}
      </div>

      <input type="hidden" name={PAYMENT_FORM_FIELD.METHOD} value={selectedMethod ?? ''} />
      <input type="hidden" name={PAYMENT_FORM_FIELD.TYPE} value={selectedType} />
      <input type="hidden" name={PAYMENT_FORM_FIELD.SEPA_ACCEPTED} value={String(sepaValues.accepted)} />
      <input type="hidden" name={PAYMENT_FORM_FIELD.TRACKING_METHOD} value={trackingMethod} />

      {isPaymentNeededInput}
      {hasPaymentMethodsRestricted && (
        <CartHintSnackbar
          variant={CART_HINT_SNACKBAR_VARIANT.BANNER}
          message={constants['i18n_not_all_payments_are_available']}
          linkText={constants['i18n_cart_action_ok']}
        />
      )}
    </>
  );
}
